import React, { useState, useMemo, useEffect } from "react";
import { Row, Col, Button, Modal, Typography, Table, Tooltip, Tag, Divider, Input, Space } from "antd";
import { FolderOpenFilled, DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import dayjs from 'dayjs';
import "./ChangeLog.css";

const { Text } = Typography;

const ChangeLog = ({ data, logs = [], top, open: controlledOpen, onClose: controlledOnClose }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isControlled = typeof controlledOpen !== 'undefined' && typeof controlledOnClose === 'function';

  const handleOpen = () => {
    if (!isControlled) setOpen(true);
  };

  const handleClose = () => {
    setSearchTerm("");
    if (isControlled) {
      controlledOnClose();
    } else {
      setOpen(false);
    }
  };

  // Sincronizar estado si es controlado
  useEffect(() => {
    if (isControlled) setOpen(controlledOpen);
  }, [controlledOpen]);

  const DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
  const isDateLike = (v) => typeof v === "string" && DATE_TIME_REGEX.test(v.trim());

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "None") return "—";

    if (isDateLike(value)) {
      const normalized = value.trim().replace(" ", "T");
      const d = dayjs(normalized);
      return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : value;
    }

    return value;
  };

  const columns = [
    {
      title: "Campo",
      dataIndex: "field",
      key: "field",
      render: (text) => <Text strong>{text}</Text>,
      width: "25%",
    },
    {
      title: "Valor Anterior",
      dataIndex: "old",
      key: "old",
      render: (value) => (
        <Tag
          color="red"
          style={{ maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word" }}
        >
          {formatValue(value)}
        </Tag>
      ),
      width: "35%",
    },
    {
      title: "Valor Nuevo",
      dataIndex: "new",
      key: "new",
      render: (value) => (
        <Tag
          color="green"
          style={{ maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word" }}
        >
          {formatValue(value)}
        </Tag>
      ),
      width: "35%",
    },
  ];

  const handleDownloadCSV = () => {
    try {
      const csvData = filteredLogs.flatMap(log =>
        log.changes.map(change => ({
          Acción: log.action,
          Fecha: dayjs(log.timestamp.replace(" ", "T")).format("DD/MM/YYYY HH:mm"),
          Usuario: log.actor || "Sistema",
          Campo: change.field,
          "Valor Anterior": formatValue(change.old),
          "Valor Nuevo": formatValue(change.new)
        }))
      );
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "historial_modificaciones.csv";
      link.click();
    } catch (error) {
      console.error("Error generando CSV:", error);
    }
  };

  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;

    const lowerSearch = searchTerm.toLowerCase();

    return logs
      .map(log => {
        const actorMatch = log.actor?.toLowerCase().includes(lowerSearch);
        const actionMatch = log.action?.toLowerCase().includes(lowerSearch);
        const dateMatch = new Date(log.timestamp).toLocaleString().toLowerCase().includes(lowerSearch);

        const filteredChanges = log.changes.filter(change =>
          change.field.toLowerCase().includes(lowerSearch) ||
          change.old?.toLowerCase().includes(lowerSearch) ||
          change.new?.toLowerCase().includes(lowerSearch)
        );

        if (actorMatch || actionMatch || dateMatch || filteredChanges.length > 0) {
          return { ...log, changes: filteredChanges.length > 0 ? filteredChanges : log.changes };
        }
        return null;
      })
      .filter(Boolean);
  }, [logs, searchTerm]);

  const displayName =
    data?.name ||
    data?.title ||
    data?.key ||
    data?.username ||
    (data?.atribute?.key && data?.value
      ? `${data.atribute.key}: ${data.value}`
      : null) ||
    data?.dependency_control?.control?.title ||
    data?.type ||
    `${data?.assetName || 'N/A'} | ${data?.dependencyName || 'N/A'}` ||
    "Sin nombre";
  const areaName = data?.area?.name || data?.asset?.area?.name || data?.responsible_area?.name || null;
  const truncatedName = displayName.length > 60 ? displayName.slice(0, 60) + "..." : displayName;

  return (
    <>
      {/* Renderizamos el ícono solo si NO estamos usando control externo */}
      {!isControlled && (
        <Tooltip title="Log de modificaciones" placement={top ? "top" : "left"}>
          <Button className="help-button" onClick={handleOpen}>
            <FolderOpenFilled style={{ color: "white" }} />
          </Button>
        </Tooltip>
      )}

      <Modal
        className="log-modal"
        open={open}
        onCancel={handleClose}
        footer={null}
        width={1000}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: "24px" }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Historial de Modificaciones</span>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadCSV}
              style={{ marginRight: 40 }}
            >
              Descargar CSV
            </Button>
          </div>
        }
      >
        <div style={{ padding: "0px 8px", marginBottom: 24 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {displayName.length > 60 ? (
                  <Tooltip title={displayName}>
                    {truncatedName}
                  </Tooltip>
                ) : (
                  displayName
                )}
              </Typography.Title>
              {areaName && (
                <Space style={{ marginTop: 4 }}>
                  <Tag color="#006939">{areaName}</Tag>
                </Space>
              )}
            </Col>
            <Col>
              <Input
                placeholder="Buscar Modificaciones"
                prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                style={{ width: 240 }}
              />
            </Col>
          </Row>
          <Divider style={{ marginTop: 16, marginBottom: 0 }} />
        </div>

        {filteredLogs.length === 0 ? (
          <Text type="secondary">No se encontraron resultados</Text>
        ) : (
          filteredLogs.map((log, index) => (
            <div key={index} style={{ marginBottom: 32 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {log.action} – {dayjs(log.timestamp.replace(" ", "T")).format("DD/MM/YYYY HH:mm:ss")}
              </Text>
              <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                Usuario: {log.actor || "Sistema"}
              </Text>

              <Table
                size="small"
                bordered
                rowKey={(row, i) => `${log.id}-${i}`}
                columns={columns}
                dataSource={log.changes}
                pagination={false}
              />

              {index < filteredLogs.length - 1 && <Divider />}
            </div>
          ))
        )}
      </Modal>
    </>
  );
};

export default ChangeLog;
